'use client';

import Image from 'next/image';
import { usePathname } from 'next/navigation';

// import ImageFacebook from '@/assets/images/icon-fb.svg';
// import ImageInstagram from '@/assets/images/icon-insta.svg';
// import ImageLinkedin from '@/assets/images/icon-linkdin.svg';
// import ImagePint from '@/assets/images/icon-pint.svg';
// import ImageYoutube from '@/assets/images/icon-youtube.svg';
import MoveToTopImage from '@/assets/images/top-arrow.svg';
import FootBanner from '@/components/V2/Footer/Banner';
import FootNote from '@/components/V2/Footer/FootNote';
import useWebAlias from '@/helpers/hooks/useWebalias';
import useWindow from '@/helpers/hooks/useWindow';
import { useAppContext } from '@/layouts/AppProvider';

import '@/styles/globals.v2.css';

import dynamic from 'next/dynamic';
import NextTopLoader from 'nextjs-toploader';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import {
  IbmPlexItalic,
  IbmPlexItalicBold,
  IbmPlexItalicMedium,
  IbmPlexItalicSemiBold,
  SweetSansPro,
  SweetSansProBold,
  SweetSansProExtraLight,
  SweetSansProLight,
  SweetSansProMedium,
  SweetSansProSemiBold,
} from '@/config/fonts';
import cn from '@/helpers/utils/classNames';
import { useAppSelector } from '@/store';
import { useGetSocialLinkListQuery } from '@/store/api/product';
import { IdentifyData } from '@/types/Klaviyo';

const KlaviyoComponent = dynamic(() => import('@/components/Klaviyo'));

const Scripts = dynamic(() => import('@/components/Scripts'));

const ToastWrapper = dynamic(() => import('@/layouts/ToastWrapper'));

export default function Footer({ isDashboard }: { isDashboard?: boolean }) {
  const pathName = usePathname();
  const window = useWindow();
  const { isBackOffice } = useAppContext();
  const { hideTopLoader } = useAppSelector(store => store.slices.petProfileSlice);
  const { customer } = useAuthContext();
  const { generateURL, webalias } = useWebAlias();
  const { socialLinks } = useGetSocialLinkListQuery(undefined, {
    selectFromResult: ({ data, isFetching, isLoading }) => ({
      socialLinks: data || [],
      isLoadingProductReviews: isFetching || isLoading,
    }),
  });

  const handleScrollToTop = () => {
    window?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const klaviyoData: IdentifyData = {
    email: customer?.email || '',
    first_name: customer?.firstName || '',
    last_name: customer?.lastName || '',
  };

  const currentURL = window?.location.origin + pathName;

  const contactUsPath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/pages/contact-us`
    : generateURL('/pages/contact-us', webalias);

  const faqPath = isBackOffice ? `https://shop.pawtree.com/${customer?.webAlias}/faq` : generateURL('/faq', webalias);

  const returnCancellationPth = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/pages/returns-and-cancellations`
    : generateURL('/pages/returns-and-cancellations', webalias);

  const shippingAndDeliveryPath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/pages/shipping-and-delivery`
    : generateURL('/pages/shipping-and-delivery', webalias);

  const meetTheTeamPath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/meet-the-team`
    : generateURL('/meet-the-team', webalias);

  const veterinaryAdvisoryCouncilPath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/veterinary-advisory-council`
    : generateURL('/veterinary-advisory-council', webalias);

  const productVideosPath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/product-videos`
    : generateURL('/product-videos', webalias);

  const givingBackPath = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias}/givingback`
    : generateURL('/givingback', webalias);

  const shareAbleText =
    'CUSTOMIZED PET NUTRITION. YOUR PETS NAME AND PHOTO ON THE BAG! A FUN, FRESH APPROACH TO PET NUTRITION AND CELEBRATING THE UNCONDITIONAL LOVE BETWEEN PETS AND PET PARENTS.';
  return (
    <footer
      className={cn(
        SweetSansProExtraLight.variable,
        SweetSansProLight.variable,
        SweetSansPro.variable,
        SweetSansProMedium.variable,
        SweetSansProSemiBold.variable,
        SweetSansProBold.variable,
        IbmPlexItalic.variable,
        IbmPlexItalicBold.variable,
        IbmPlexItalicMedium.variable,
        IbmPlexItalicSemiBold.variable,
        'v2-relative v2-mt-[135px] md:v2-mt-[135px] xs:v2-mt-[111px] sm:v2-mt-[111px] main-page-footer',
        { 'xs:!v2-mt-[0px] sm:!v2-mt-[0px]': pathName.includes('checkoutexperience') }
      )}
    >
      <div className="back-to-top-btn v2-h-14 v2-z-10 v2-right-0 v2-w-14 v2-flex v2-items-center v2-content-center v2-bg-primary-600 v2-rounded-xs v2-cursor-pointer md:v2-hidden sm:v2-flex xs:v2-flex v2-sticky v2-top-[500px] v2-ml-auto v2-mt-[-15px] v2-mb-[15px] v2-mr-[15px] v2-border v2-border-[#fff]">
        {' '}
        <Image
          src={MoveToTopImage}
          alt="Move to top"
          className="mx-auto block h-6 w-6 cursor-pointer"
          onClick={handleScrollToTop}
        />
      </div>
      {!isBackOffice && <FootBanner.Form />}
      <FootBanner>
        <FootBanner.Branding />

        <FootBanner.NavigationLinks>
          <FootBanner.NavigationLinks.Link href={contactUsPath} label="Contact Us" as={'a'} />
          <FootBanner.NavigationLinks.Link href={faqPath} label="FAQ" as={'a'} />
          <FootBanner.NavigationLinks.Link href={shippingAndDeliveryPath} as={'a'} label="Shipping & Delivery" />
          <FootBanner.NavigationLinks.Link href={returnCancellationPth} as={'a'} label="Returns & Cancellation" />
          <FootBanner.NavigationLinks.Link href={meetTheTeamPath} as={'a'} label="Meet the team" />
          <FootBanner.NavigationLinks.Link href={veterinaryAdvisoryCouncilPath} as={'a'} label="Vet Advisory Council" />
          <FootBanner.NavigationLinks.Link href={productVideosPath} as={'a'} label="Videos" />
          <FootBanner.NavigationLinks.Link href={givingBackPath} as={'a'} label="Giving Back" />
        </FootBanner.NavigationLinks>

        <FootBanner.SocialLinks>
          {socialLinks.map(item => (
            <FootBanner.SocialLinks.Link href={item.link} key={item.link}>
              {/* <img src={item.icon} alt={item.platform} /> */}
              <Image src={item.icon} alt={item.platform} unoptimized width={28} height={28} className="!v2-w-full" />
            </FootBanner.SocialLinks.Link>
          ))}
          {/* <FootBanner.SocialLinks.Link href="https://www.facebook.com/pawtree">
            <Image src={ImageFacebook} alt="Facebook" />
          </FootBanner.SocialLinks.Link> */}

          {/* <FootBanner.SocialLinks.Link href="https://twitter.com/pawtree">
            <Image src={ImageTwitter} alt="Twitter" />
          </FootBanner.SocialLinks.Link> */}
          {/* <FootBanner.SocialLinks.Link href="https://www.instagram.com/pawtree/">
            <Image src={ImageInstagram} alt="Instagram" />
          </FootBanner.SocialLinks.Link>
          <FootBanner.SocialLinks.Link href="https://www.youtube.com/user/PawTreePets">
            <Image src={ImageYoutube} alt="Youtube" />
          </FootBanner.SocialLinks.Link>
          <FootBanner.SocialLinks.Link href="https://www.pinterest.com/pawtree/">
            <Image src={ImagePint} alt="Pint Rest" />
          </FootBanner.SocialLinks.Link>
          <FootBanner.SocialLinks.Link href="https://www.linkedin.com/company/pawtree">
            <Image src={ImageLinkedin} alt="Linkedin" />
          </FootBanner.SocialLinks.Link> */}
          {/* <FootBanner.SocialLinks.Link href="https://www.snapchat.com/add/pawtreepets">
            <Image src={ImageSnapchat} alt="Snapchat" />
          </FootBanner.SocialLinks.Link> */}
        </FootBanner.SocialLinks>
      </FootBanner>
      <FootNote />
      <ToastWrapper />
      {!hideTopLoader && <NextTopLoader />}
      <KlaviyoComponent type={'profiles'} data={klaviyoData} />
      <Scripts />
    </footer>
  );
}
